/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { Menu, MenuButton, MenuItems, MenuItem, Transition } from '@headlessui/react'
import { ChevronDownIcon, ChevronUpIcon, TrashIcon } from '@heroicons/react/20/solid'
import { twMerge as mergeClassNames } from 'tailwind-merge'
import { Controller, useForm } from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'

// Components
import {
  AlignmentControls,
  BackgroundControls,
  BorderControls,
  FontStyleControls,
  SizeControls,
  TextAlignControls,
} from '../../components/BadgeSettings'
import { Button } from '../../components/Button'
import { ColorPicker } from '../../components/ColorPicker'
import { MergeFieldLogicFields } from '../../components/MergeFieldLogicFields'
import { Modal } from '../../components/Modal'
import { RadioInput } from '../../components/RadioInput'
import { Select } from '../../components/Select'
import { TextInput } from '../../components/TextInput'
import { Toggle } from '../../components/Toggle'
import { WrapTextIcon } from '../../components/WrapTextIcon'

// Images
import Edit from '../../assets/images/editCircle.svg'

// Utils & Service
import { getGlobalCustomFonts, getGoogleFonts } from '../../services/settings.service'
import { INCLUSION_OPTIONS, NON_SYNCED_FIELDS } from '../../utils/constants'
import {
  DEFAULT_REPEAT_ROWS,
  LIST_LINE_TYPES,
  MAX_LIST_LINES,
  MAX_REPEAT_ROWS,
  buildListLine,
} from '../../utils/badgeList'
import { configureMergeFieldOptions, getLogicRuleValue, mapLogicValues } from '../../utils/helpers'
import { findElementLocation } from './helpers'

const DEFAULT_LOGIC = {
  inclusionLogic: INCLUSION_OPTIONS[0],
  mergeField: null,
  operator: null,
  value: null,
}

/**
 *
 * BadgeElementSettings
 *
 */
const BadgeElementSettings = ({
  activeElement,
  admissionItems,
  availableCustomFields,
  badgeHeight,
  badgeWidth,
  categories,
  config,
  copyElement,
  createGroup,
  currentPanelIndex,
  deleteElement,
  duplicateElement,
  enabledCustomFields,
  layers,
  moveElement,
  oilTypeOptions = null,
  panels,
  temporaryGroup,
  registrationType = 'manual',
  sessions,
  setCustomFonts,
  setGoogleFonts,
  updateElement,
}) => {
  // State
  const [fonts, setFonts] = useState([])
  const [loadingFonts, setLoadingFonts] = useState(false)
  const [selectedLayer, setSelectedLayer] = useState(null)
  const [showVisibilityModal, setShowVisibilityModal] = useState(false)

  const mergeFieldOptions = useMemo(() => {
    let mergeFieldLabels = NON_SYNCED_FIELDS
    if (enabledCustomFields) {
      const customFields = _.map(enabledCustomFields, (f) => availableCustomFields[f]?.text)
      mergeFieldLabels = _.concat(NON_SYNCED_FIELDS, customFields)
    }

    return configureMergeFieldOptions(
      mergeFieldLabels,
      availableCustomFields,
      categories,
      admissionItems,
      sessions,
      registrationType === 'Cvent',
      oilTypeOptions,
    )
  }, [
    enabledCustomFields,
    availableCustomFields,
    categories,
    admissionItems,
    sessions,
    registrationType,
    oilTypeOptions,
  ])

  const categoryOptions = _.map(_.keys(categories), (k) => ({
    id: k,
    label: categories[k],
  }))

  // Custom QR content textarea + last known caret position, so merge fields can
  // be inserted at the cursor rather than appended.
  const qrContentRef = useRef(null)
  const qrCaretRef = useRef(null)

  /**
   * Inserts a `{{ accessor }}` merge-field token into the custom QR content at
   * the current caret position (or the end if the field hasn't been focused).
   * @param {string} accessor
   */
  const insertQrMergeField = (accessor) => {
    const content = activeElement?.attrs?.qrContent || ''
    const caret = qrCaretRef.current
    const start = caret ? caret.start : content.length
    const end = caret ? caret.end : content.length
    const token = `{{ ${accessor} }}`
    const newContent = `${content.slice(0, start)}${token}${content.slice(end)}`

    updateElement({ qrContent: newContent })

    // Restore focus + caret just after the inserted token on the next frame,
    // once the controlled textarea has re-rendered with the new value.
    const nextPos = start + token.length
    qrCaretRef.current = { start: nextPos, end: nextPos }
    requestAnimationFrame(() => {
      const el = qrContentRef.current
      if (el) {
        el.focus()
        el.setSelectionRange(nextPos, nextPos)
      }
    })
  }

  const {
    control,
    clearErrors,
    handleSubmit,
    formState: { errors },
    register,
    getValues,
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      id: null,
      always: true,
      inclusionLogic: INCLUSION_OPTIONS[0],
      visibilityLogic: [DEFAULT_LOGIC],
      lineLogic: [DEFAULT_LOGIC],
    },
  })

  /* ---------------------------------------------------------------------- *
   * List line items
   * ---------------------------------------------------------------------- */

  /**
   * The line currently open in the editor, kept as component state rather than
   * in the element form: that form is reset whenever the selection changes, and
   * a half-edited line should neither survive nor be destroyed by that.
   * `lineDraftIndex` is null when the draft is a new line.
   */
  const [lineDraft, setLineDraft] = useState(null)
  const [lineDraftIndex, setLineDraftIndex] = useState(null)
  const [showLineModal, setShowLineModal] = useState(false)

  // Line content textarea + last known caret, so merge fields land at the
  // cursor rather than being appended — same behaviour as the QR content field.
  const lineContentRef = useRef(null)
  const lineCaretRef = useRef(null)

  const listLines = activeElement?.attrs?.lines || []

  /**
   * Applies a change to the draft line being edited.
   * @param {object} update
   */
  const updateLineDraft = (update) => setLineDraft((current) => ({ ...current, ...update }))

  /**
   * Inserts a `{{ accessor }}` token into the draft line's content at the caret.
   * @param {string} accessor
   */
  const insertLineMergeField = (accessor) => {
    const content = lineDraft?.content || ''
    const caret = lineCaretRef.current
    const start = caret ? caret.start : content.length
    const end = caret ? caret.end : content.length
    const token = `{{ ${accessor} }}`

    updateLineDraft({ content: `${content.slice(0, start)}${token}${content.slice(end)}` })

    const nextPos = start + token.length
    lineCaretRef.current = { start: nextPos, end: nextPos }
    requestAnimationFrame(() => {
      const el = lineContentRef.current
      if (el) {
        el.focus()
        el.setSelectionRange(nextPos, nextPos)
      }
    })
  }

  /**
   * Opens the line editor on an existing line, or on a new one when `index` is
   * null. The logic builder is seeded with the line's own rules so it opens on
   * what is already configured.
   * @param {number|null} index
   */
  const openLineEditor = (index) => {
    const line =
      index === null ? buildListLine(LIST_LINE_TYPES.TEXT, `${uuidv4()}`) : { ...listLines[index] }

    let lineLogic = [DEFAULT_LOGIC]
    if (_.isArray(line.logic) && line.logic.length > 0) {
      lineLogic = mapLogicValues(
        line.logic,
        mergeFieldOptions,
        availableCustomFields,
        categories,
        admissionItems,
        sessions,
        oilTypeOptions,
      )
    }

    setValue('lineLogic', lineLogic)
    setLineDraft(line)
    setLineDraftIndex(index)
    setShowLineModal(true)
  }

  /**
   * Closes the line editor.
   *
   * The draft is intentionally left in place: the modal animates out over a
   * couple of hundred milliseconds and clearing it here would blank the panel
   * mid-transition. `openLineEditor` always replaces it anyway.
   */
  const closeLineEditor = () => setShowLineModal(false)

  /**
   * Whether the draft is complete enough to save. An incomplete rule would
   * evaluate false and silently hide the line, so saving is blocked instead.
   */
  const draftLogic = watch('lineLogic')
  const lineDraftValid = useMemo(() => {
    if (!lineDraft) return false

    if (lineDraft.type === LIST_LINE_TYPES.REPEAT) {
      if (!lineDraft.field) return false
    } else if (`${lineDraft.content || ''}`.trim() === '') return false

    if (lineDraft.logic === 'always') return true

    return (
      _.isArray(draftLogic) &&
      draftLogic.length > 0 &&
      _.every(draftLogic, (l) => l.mergeField && l.operator)
    )
  }, [lineDraft, draftLogic])

  /**
   * Commits the draft back onto the element's `lines`.
   */
  const saveLineDraft = () => {
    let logic = 'always'
    if (lineDraft.logic !== 'always') {
      logic = _.map(draftLogic, (l) => ({
        ...l,
        mergeField: l.mergeField?.id,
        operator: l.operator?.id,
        inclusionLogic: l.inclusionLogic?.id,
        value: getLogicRuleValue(l),
      }))
    }

    const lines = [...listLines]
    const line = { ...lineDraft, logic }

    if (lineDraftIndex === null) lines.push(line)
    else lines[lineDraftIndex] = line

    updateElement({ lines })
    closeLineEditor()
  }

  /**
   * Moves a line up or down within the object. Order is print order.
   * @param {number} index
   * @param {number} offset -1 to move up, 1 to move down
   */
  const moveListLine = (index, offset) => {
    const target = index + offset
    if (target < 0 || target >= listLines.length) return

    const lines = [...listLines]
    const [moved] = lines.splice(index, 1)
    lines.splice(target, 0, moved)

    updateElement({ lines })
  }

  /**
   * Removes a line from the object.
   * @param {number} index
   */
  const deleteListLine = (index) => {
    const lines = [...listLines]
    lines.splice(index, 1)

    updateElement({ lines })
  }

  /**
   * A one-line summary of how a configured line will print, for the settings list.
   * @param {object} line
   * @returns {string}
   */
  const describeListLine = (line) => {
    if (line.type === LIST_LINE_TYPES.REPEAT) {
      const label = line.fieldLabel || line.field || 'No field selected'
      return `Repeat: ${label} (up to ${line.maxRows || DEFAULT_REPEAT_ROWS})`
    }

    return line.content || '(empty)'
  }

  useEffect(() => {
    const getGlobalFonts = async () => {
      setLoadingFonts(true)

      let options = []
      // Pull Google Fonts
      const data = await getGoogleFonts(
        () => {},
        () => {},
      )
      if (data) {
        const filteredFonts = _.map(
          _.filter(
            data.items,
            (i) => i.variants.includes('regular') && i.variants.includes('italic'),
          ),
          (f) => ({
            id: f.family,
            label: f.family,
            ...f,
          }),
        )

        options = filteredFonts
      }

      // Pull Custom Fonts
      const globalFonts = await getGlobalCustomFonts(
        () => {},
        () => {},
      )

      if (globalFonts) {
        options = [
          ..._.map(globalFonts, (f) => ({ id: f.id, label: f.name, url: f.signedUrl })),
          ...options,
        ]
      }

      setLoadingFonts(false)
      setFonts(options)
    }

    getGlobalFonts()
  }, [])

  /**
   * Tracks the visibility of the `activeElement`
   * - If the element is always visible, set the visibility to `always`
   * - If the element is not always visible, set the visibility to the field, operator, and value
   */
  useEffect(() => {
    // If there's no active element, reset the form
    if (!activeElement) {
      reset({ id: null, always: true, visibilityLogic: [] })
    }
    // If `activeElement` is a layer and visibility is `always`
    else if (activeElement.className === 'Layer' && activeElement.attrs.visibility === 'always') {
      reset({ id: activeElement.attrs.id, always: true, visibilityLogic: [] })
    }
    // If `activeElement` is a layer and visibility is configured
    else if (activeElement.className === 'Layer') {
      let categoryId

      // Handle cases where visibility is a string - this is to make our new logic
      // backwards compatible
      if (_.isString(activeElement.attrs.visibility)) {
        // eslint-disable-next-line prefer-destructuring
        categoryId = activeElement.attrs.visibility.split(':')[2]
      }
      // Otherwise, just use the id set
      else {
        categoryId = activeElement.attrs.visibility[0].value
      }

      reset({
        id: activeElement.attrs.id,
        always: false,
        inclusionLogic: INCLUSION_OPTIONS[0],
        visibilityLogic: mapLogicValues(
          [
            {
              inclusionLogic: INCLUSION_OPTIONS[0].id,
              mergeField: 'categoryId',
              operator: 'is',
              value: categoryId,
            },
          ],
          mergeFieldOptions,
          availableCustomFields,
          categories,
          null,
          null,
          oilTypeOptions,
        ),
      })
    }
    // If `activeElement` is not a layer and visibility is `always`
    else if (activeElement.attrs.visibility === 'always') {
      reset({ id: activeElement.attrs.id, always: true, visibilityLogic: [] })
    }
    // If `activeElement` is not a layer and visibility is configured as the old format
    // Handle cases where visibility is a string - this is to make our new logic
    // backwards compatible
    else if (_.isString(activeElement.attrs.visibility)) {
      const visibility = activeElement.attrs.visibility.split(':')

      reset({
        id: activeElement.attrs.id,
        always: false,
        inclusionLogic: INCLUSION_OPTIONS[0],
        visibilityLogic: mapLogicValues(
          [
            {
              inclusionLogic: INCLUSION_OPTIONS[0].id,
              mergeField: visibility[0],
              operator: visibility[1],
              value: visibility[2],
            },
          ],
          mergeFieldOptions,
          availableCustomFields,
          categories,
          admissionItems,
          sessions,
          oilTypeOptions,
        ),
      })
    }
    // Otherwise, configure the visibilty logic
    else {
      reset({
        id: activeElement.attrs.id,
        always: false,
        inclusionLogic: _.find(
          INCLUSION_OPTIONS,
          (o) => o.id === activeElement.attrs.visibility[0].inclusionLogic,
        ),
        visibilityLogic: mapLogicValues(
          activeElement.attrs.visibility,
          mergeFieldOptions,
          availableCustomFields,
          categories,
          admissionItems,
          sessions,
          oilTypeOptions,
        ),
      })
    }

    if (activeElement && activeElement?.className !== 'Layer') {
      const location = findElementLocation(config, activeElement.attrs.id)
      setSelectedLayer(layers[location.layerIndex])
    }
  }, [activeElement])

  const renderGroupButton = () => {
    // Check that the elements in the temporary group are on the same layer
    const location = findElementLocation(config, temporaryGroup[0].attrs.id)
    const sameLayer = _.every(temporaryGroup, (element) => {
      const l = findElementLocation(config, element.attrs.id)
      return l.layerIndex === location.layerIndex
    })

    if (_.every(temporaryGroup, (el) => el.attrs.inGroup)) return null

    return (
      <div className="flex">
        <Button
          disabled={!sameLayer} // Disable button if elements are not on the same layer
          fullWidth
          label="Create Group"
          onClick={createGroup}
          title={!sameLayer ? 'Elements must be on the same layer to group.' : undefined}
        />
      </div>
    )
  }

  const renderVisibilityLogic = () => {
    const logic = _.map(getValues('visibilityLogic'), (l) => ({
      ...l,
      mergeField: l.mergeField?.id,
      operator: l.operator?.id,
      inclusionLogic: l.inclusionLogic?.id,
      value: getLogicRuleValue(l),
    }))

    return (
      <div className="flex flex-col gap-1">
        {_.map(logic, (l, i) => {
          const mergeField = _.find(mergeFieldOptions, (o) => o.id === l.mergeField)
          let optionValue = l.value

          if (mergeField?.label === 'Category') {
            const category = categories[optionValue]
            optionValue = category || optionValue
          } else if (mergeField?.label === 'Admission Item') {
            const admissionItem = admissionItems[optionValue]
            optionValue = admissionItem?.name || optionValue
          } else if (mergeField?.label === 'Sessions') {
            const sessionItem = sessions[optionValue]
            optionValue = sessionItem?.name || optionValue
          } else if (mergeField?.label === 'OIL Type') {
            const oilType = oilTypeOptions?.[optionValue]
            optionValue = oilType?.name || optionValue
          }

          return (
            <div
              className={mergeClassNames(
                'flex flex-row items-center justify-between gap-2',
                i !== logic.length - 1 && 'border-b border-gray-300 pb-2',
              )}
              key={l.id}
            >
              <div className="flex flex-row gap-1 text-xs">
                <span>{`{{ ${mergeField?.label || 'Unknown'} }}`}</span>
                <span>{l.operator}</span>
                <span className="font-bold">{optionValue}</span>
              </div>

              {i !== logic.length - 1 && (
                <span className="flex items-center rounded-full bg-purple-100 px-1 py-0.5 text-[8px] font-bold text-purple">
                  {l.inclusionLogic}
                </span>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  /**
   * Typography controls shared by every element that draws text.
   *
   * Text elements and list objects style their text identically — font, size,
   * colour, weight, alignment, casing — so they render the same controls. Only
   * the wrap toggle differs: a list always wraps, since long lines flowing onto
   * the next line is the whole point.
   * @param {boolean} showWrapToggle
   */
  const renderTypeControls = (showWrapToggle) => {
    const selectedFont = fonts.find((f) => {
      if (f.id === activeElement.attrs.fontId || f.family === activeElement.attrs.fontFamily)
        return f
      return null
    })

    return (
      <div className="space-y-1.5">
        <Select
          className="rounded-2xl border-gray-550 py-2.5 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
          data-testid="fontFamily"
          disabled={loadingFonts}
          fullWidth
          id="fontFamily"
          name="fontFamily"
          nunito
          onChange={(newFont) => {
            // Only update google fonts for selected google fonts
            if (!newFont.url) {
              setGoogleFonts({
                font: newFont.family,
                weights: newFont.variants,
              })

              updateElement({ fontFamily: newFont.family, fontId: null })
            } else {
              setCustomFonts(newFont.id)
              updateElement({ fontFamily: newFont.label, fontId: newFont.id })
            }
          }}
          options={fonts}
          search
          style={{ flex: true, width: '100%' }}
          value={selectedFont}
        />

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TextInput
              type="number"
              className="w-[80px] rounded-2xl border-gray-550 py-2 pl-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
              id="fontSize"
              name="fontSize"
              min={1}
              max={100}
              onChange={(event) => updateElement({ fontSize: parseInt(event.target.value, 10) })}
              placeholder="Font Size"
              value={activeElement.attrs.fontSize}
            />

            <ColorPicker
              color={activeElement.attrs.fill}
              onChange={(color) => updateElement({ fill: color.hex })}
              tooltip="Text color"
            />
          </div>

          <FontStyleControls element={activeElement} updateElement={updateElement} />
        </div>

        <div className="flex justify-between">
          <TextAlignControls element={activeElement} updateElement={updateElement} />

          <div className="flex">
            <button
              onClick={() => {
                updateElement({ uppercase: !activeElement.attrs.uppercase })
              }}
              className={mergeClassNames(
                'flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-transparent hover:bg-purple-400',
                activeElement.attrs.uppercase && 'bg-purple-400 hover:bg-purple-200',
              )}
              aria-label="Format uppercase"
              title="Format uppercase"
              type="button"
            >
              <box-icon type="regular" color="black" name="font-size" size="sm" />
            </button>

            {showWrapToggle && (
              <button
                type="button"
                className={mergeClassNames(
                  'flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-transparent hover:bg-purple-400',
                  activeElement.attrs.wrap === 'word' && 'bg-purple-400 hover:bg-purple-200',
                )}
                aria-label="Wrap text"
                title="Wrap text"
                onClick={() => {
                  updateElement({ wrap: activeElement.attrs.wrap === 'word' ? 'none' : 'word' })
                }}
              >
                <WrapTextIcon />
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  /**
   * The configured lines of a list object, in print order.
   *
   * Each row is a summary — the full content, field, logic and emphasis are
   * edited in the line modal, which is where the logic builder lives.
   */
  const renderListLines = () => (
    <div className="space-y-1">
      <span className="text-sm font-bold">Line Items</span>

      {listLines.length === 0 && (
        <p className="text-xs italic text-gray-600">
          No lines yet. Nothing will print until you add one.
        </p>
      )}

      <div className="flex max-h-[400px] flex-col gap-1 overflow-y-auto">
        {_.map(listLines, (line, i) => (
          <div
            className="flex items-center justify-between gap-1 rounded-lg bg-white px-2 py-1.5"
            key={line.id}
          >
            <button
              className="min-w-0 flex-1 text-left"
              onClick={() => openLineEditor(i)}
              title="Edit line"
              type="button"
            >
              <span className="block truncate text-xs">{describeListLine(line)}</span>

              {line.logic !== 'always' && (
                <span className="text-[10px] font-bold uppercase text-purple">Conditional</span>
              )}
            </button>

            <button
              aria-label="Move line up"
              className="disabled:opacity-25"
              disabled={i === 0}
              onClick={() => moveListLine(i, -1)}
              title="Move up"
              type="button"
            >
              <ChevronUpIcon className="h-4 w-4" />
            </button>

            <button
              aria-label="Move line down"
              className="disabled:opacity-25"
              disabled={i === listLines.length - 1}
              onClick={() => moveListLine(i, 1)}
              title="Move down"
              type="button"
            >
              <ChevronDownIcon className="h-4 w-4" />
            </button>

            <button
              aria-label="Delete line"
              onClick={() => deleteListLine(i)}
              title="Delete line"
              type="button"
            >
              <TrashIcon className="h-4 w-4 text-red" />
            </button>
          </div>
        ))}
      </div>

      {listLines.length < MAX_LIST_LINES ? (
        <Button fullWidth label="Add Line" onClick={() => openLineEditor(null)} />
      ) : (
        <p className="text-xs italic text-gray-600">Maximum of {MAX_LIST_LINES} lines reached.</p>
      )}
    </div>
  )

  const renderSettings = () => {
    let title = ''
    let settingsSection = null
    let visibilitySection = null
    let moveSection = null
    let customSection = null

    // If the `activeElement` doesn't match the form, return null
    // to prevent any issues with timing state updates.
    if (activeElement.attrs.id !== getValues('id')) return null

    if (activeElement.className === 'Layer') {
      title = 'Layer'

      const layerName = activeElement.attrs.name || ''

      settingsSection = (
        <TextInput
          className="rounded-2xl border-gray-550 py-2 pl-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
          id="name"
          name="name"
          onChange={(event) => {
            updateElement({
              name: event.target.value,
            })
          }}
          placeholder="Layer Name"
          value={layerName}
        />
      )

      if (categories) {
        const options = _.concat([{ id: '-1', label: 'Always Visible' }], categoryOptions)

        visibilitySection = (
          <div className="space-y-1">
            <span className="text-sm font-semibold">Visibility</span>

            <Select
              className="rounded-2xl border-gray-550 py-2.5 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
              fullWidth
              id="visibility"
              nunito
              onChange={({ id }) => {
                updateElement({
                  visibility: id === '-1' ? 'always' : `category:is:${id}`,
                })
              }}
              options={options}
              label="Layer Visible for Category"
              placeholder="Select a Category"
              style={{ flex: true, width: '100%' }}
              value={
                getValues('always')
                  ? options[0]
                  : _.find(options, (o) => o.id === activeElement.attrs.visibility.split(':')[2])
              }
            />
          </div>
        )
      }
    } else if (activeElement.attrs.customQr) {
      title = 'Custom QR Code'

      settingsSection = (
        <div className="flex flex-col space-y-5">
          <div className="space-y-1.5">
            <span className="text-sm font-semibold">QR Content</span>

            <textarea
              ref={qrContentRef}
              className="w-full rounded-2xl border border-gray-550 px-4 py-2 text-sm placeholder:font-normal placeholder:text-gray-600 focus:border-purple focus:outline-none"
              onChange={(event) => {
                qrCaretRef.current = {
                  start: event.target.selectionStart,
                  end: event.target.selectionEnd,
                }
                updateElement({ qrContent: event.target.value })
              }}
              onSelect={(event) => {
                qrCaretRef.current = {
                  start: event.target.selectionStart,
                  end: event.target.selectionEnd,
                }
              }}
              placeholder="e.g. https://www.executivevents.com/ or User: {{ firstName }}"
              rows={3}
              value={activeElement.attrs.qrContent || ''}
            />

            <p className="text-xs text-gray-600">Insert merge fields to embed attendee data.</p>
          </div>

          <Menu as="div" className="relative inline-block w-full">
            <MenuButton className="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-400">
              Insert Merge Field
            </MenuButton>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <MenuItems className="absolute left-0 z-10 mt-2 max-h-60 w-full overflow-auto rounded-md bg-white shadow-lg">
                <div className="flex flex-col">
                  {_.map(mergeFieldOptions, (option) => (
                    <MenuItem
                      className="text-primary cursor-pointer select-none px-3 py-2 text-left text-sm hover:bg-purple hover:text-white"
                      key={`qr-merge-${option.id}`}
                      onClick={() => insertQrMergeField(option.id)}
                    >
                      <span>{option.label}</span>
                    </MenuItem>
                  ))}
                </div>
              </MenuItems>
            </Transition>
          </Menu>

          <SizeControls element={activeElement} updateElement={updateElement} />

          <AlignmentControls
            containerHeight={badgeHeight}
            containerWidth={badgeWidth}
            element={activeElement}
            isInGroup={activeElement.attrs.inGroup || false}
            moveElement={moveElement}
            updateElement={updateElement}
          />
        </div>
      )
    } else if (activeElement.attrs.qrImage) {
      title = 'App QR Code'

      const options = [
        {
          id: 'no-print',
          label: 'No QR Code',
        },
        { id: 'scrambled', label: 'Scrambled QR Code' },
      ]

      settingsSection = (
        <div className="flex">
          <AlignmentControls
            containerHeight={badgeHeight}
            containerWidth={badgeWidth}
            element={activeElement}
            isInGroup={activeElement.attrs.inGroup || false}
            moveElement={moveElement}
            updateElement={updateElement}
          />
        </div>
      )

      customSection = (
        <RadioInput
          className="mt-4 self-center"
          label="If Attendee Opts Out, Print:"
          labelClassName="text-sm font-semibold"
          options={options}
          onChange={(visibility) => updateElement({ qrPrint: visibility })}
          value={_.find(options, (o) => activeElement.attrs.qrPrint === o.id).id}
        />
      )
    } else if (activeElement.className === 'Image') {
      title = 'Image'

      settingsSection = (
        <div className="flex flex-col space-y-5">
          {!activeElement.attrs.isBackground && (
            <>
              <SizeControls element={activeElement} updateElement={updateElement} />
              <BorderControls element={activeElement} updateElement={updateElement} />
            </>
          )}

          <BackgroundControls element={activeElement} updateElement={updateElement} />

          {!activeElement.attrs.isBackground && (
            <AlignmentControls
              containerHeight={badgeHeight}
              containerWidth={badgeWidth}
              element={activeElement}
              isInGroup={activeElement.attrs.inGroup || false}
              moveElement={moveElement}
              updateElement={updateElement}
            />
          )}
        </div>
      )
    } else if (activeElement.className === 'Text') {
      title = 'Text Field'

      settingsSection = (
        <div className="flex flex-col space-y-5">
          {renderTypeControls(true)}

          <AlignmentControls
            containerHeight={badgeHeight}
            containerWidth={badgeWidth}
            element={activeElement}
            isInGroup={activeElement.attrs.inGroup || false}
            moveElement={moveElement}
            updateElement={updateElement}
          />
        </div>
      )
    } else if (activeElement.className === 'List') {
      title = 'List'

      settingsSection = (
        <div className="flex flex-col space-y-5">
          {/* The lines are what this element is; styling comes after. */}
          {renderListLines()}

          <hr className="border-gray-550" />

          {renderTypeControls(false)}

          <div className="space-y-1">
            <span className="text-sm font-bold">Line Spacing</span>

            <TextInput
              className="w-[80px] rounded-2xl border-gray-550 py-2 pl-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
              id="lineSpacing"
              max={40}
              min={0}
              name="lineSpacing"
              onChange={(event) =>
                updateElement({ lineSpacing: parseInt(event.target.value, 10) || 0 })
              }
              placeholder="Spacing"
              type="number"
              value={activeElement.attrs.lineSpacing ?? 0}
            />

            <p className="text-xs text-gray-600">Extra space between lines, in pixels.</p>
          </div>

          <div className="space-y-1">
            <span className="text-sm font-bold">Background</span>
            <ColorPicker
              color={activeElement.attrs.backgroundFill}
              onChange={(color) => updateElement({ backgroundFill: color.hex })}
              transparentOption
              tooltip="Background color"
            />
          </div>

          {/*
            No border control. The list renders `stroke` and `strokeWidth` on its
            background rect, and both attrs still exist with a width of 0, so
            restoring the control is a one-line change if it is ever wanted.
          */}

          <SizeControls element={activeElement} updateElement={updateElement} />

          <p className="text-xs text-gray-600">
            Height is a minimum — lines that wrap or repeat grow the object downward rather than
            being cut off.
          </p>

          <AlignmentControls
            containerHeight={badgeHeight}
            containerWidth={badgeWidth}
            element={activeElement}
            isInGroup={activeElement.attrs.inGroup || false}
            moveElement={moveElement}
            updateElement={updateElement}
          />
        </div>
      )
    } else if (activeElement.className === 'Rect' || activeElement.className === 'Group') {
      title = activeElement.className === 'Rect' ? 'Rectangle' : 'Group'

      settingsSection = (
        <div className="flex flex-col space-y-5">
          <div className="space-y-1">
            <span className="text-sm font-bold">Background</span>
            <ColorPicker
              color={activeElement.attrs.fill}
              onChange={(color) => updateElement({ fill: color.hex })}
              transparentOption
              tooltip="Background color"
            />
          </div>

          <BorderControls element={activeElement} updateElement={updateElement} />

          <AlignmentControls
            containerHeight={badgeHeight}
            containerWidth={badgeWidth}
            element={activeElement}
            moveElement={moveElement}
            updateElement={updateElement}
          />
        </div>
      )
    }

    if (activeElement.className !== 'Layer' && !activeElement.attrs.isBackground) {
      visibilitySection = (
        <div className="space-y-1">
          <span className="text-sm font-bold">Visibility</span>

          <Controller
            name="always"
            control={control}
            render={({ field: { value } }) => (
              <Toggle
                checked={value}
                onChange={(checked) => {
                  if (checked) {
                    setValue('always', true)
                    updateElement({ visibility: 'always' })
                  } else {
                    setValue('visibilityLogic', [DEFAULT_LOGIC])
                    setShowVisibilityModal(true)
                  }
                }}
                label="Always Visible"
                labelClassName="text-sm"
              />
            )}
          />

          {!watch('always') && (
            <div className="flex flex-col gap-3 pt-2">
              {renderVisibilityLogic()}

              <Button fullWidth label="Edit Logic" onClick={() => setShowVisibilityModal(true)} />
            </div>
          )}
        </div>
      )

      moveSection = (
        <div className="space-y-1">
          <span className="text-sm font-bold">Placement</span>

          <div className="space-y-2">
            {layers.length > 1 && (
              <>
                <Select
                  className="rounded-2xl border-gray-550 py-2.5 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                  fullWidth
                  id="placement"
                  label="Change Layer"
                  nunito
                  onChange={({ id }) => {
                    // `id` is the layer index
                    moveElement('layer', id)
                  }}
                  options={layers}
                  style={{ flex: true, width: '100%' }}
                  value={selectedLayer}
                />

                <Menu as="div" className="inline-block w-full">
                  <MenuButton className="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-400">
                    Copy to another layer
                  </MenuButton>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <MenuItems className="absolute right-0 z-10 mt-2 w-56 origin-top-right overflow-hidden rounded-md bg-white shadow-lg">
                      <div className="flex flex-col">
                        {_.map(
                          _.filter(layers, (l) => l.id !== selectedLayer?.id),
                          (l) => (
                            <MenuItem
                              className="text-primary cursor-pointer select-none px-3 py-2 text-sm hover:bg-purple hover:text-white"
                              key={`label-${l.id}`}
                              onClick={() => {
                                // `id` is the layer index
                                copyElement('layer', l.id)
                              }}
                            >
                              <span>Copy to {l.label}</span>
                            </MenuItem>
                          ),
                        )}
                      </div>
                    </MenuItems>
                  </Transition>
                </Menu>
              </>
            )}

            {panels.length > 2 && (
              <Menu as="div" className="inline-block w-full">
                <MenuButton className="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-400">
                  Copy to another panel
                </MenuButton>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <MenuItems className="absolute right-0 z-10 mt-2 w-56 origin-top-right overflow-hidden rounded-md bg-white shadow-lg">
                    <div className="flex flex-col">
                      {_.map(
                        _.filter(
                          panels,
                          (p) => p.label !== '+ Add Panel' && p.id !== currentPanelIndex,
                        ),
                        (p) => (
                          <MenuItem
                            className="text-primary cursor-pointer select-none px-3 py-2 text-sm hover:bg-purple hover:text-white"
                            key={`panel-${p.id}`}
                            onClick={() => {
                              // `id` is the panel index
                              copyElement('panel', p.id)
                            }}
                          >
                            <span>Copy to {p.label}</span>
                          </MenuItem>
                        ),
                      )}
                    </div>
                  </MenuItems>
                </Transition>
              </Menu>
            )}
          </div>
        </div>
      )
    }

    return (
      <div className="flex flex-col pb-3">
        <span className="text-sm font-bold uppercase">{title}</span>

        <div className="space-y-5">
          {/* Settings */}
          {settingsSection}

          {/* Visibility */}
          {!activeElement.attrs.inGroup && visibilitySection}

          {/* Custom Settings */}
          {customSection}

          {/* Move Element */}
          {!activeElement.attrs.inGroup && (layers.length > 1 || panels.length > 2) && moveSection}

          <div className="flex w-full flex-row gap-4">
            <Button
              background="bg-purple"
              fullWidth
              label="Duplicate"
              onClick={duplicateElement}
            />

            <Button
              background="bg-red"
              fullWidth
              label={activeElement.className === 'Group' ? 'Remove Group' : 'Delete'}
              onClick={deleteElement}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="z-0 h-full w-[30%] overflow-y-auto rounded-xl bg-gray-400 px-4 pt-3">
      {activeElement !== null && <div className="h-full w-full">{renderSettings()}</div>}

      {temporaryGroup &&
        _.filter(temporaryGroup, (el) => el.className !== 'Group').length > 1 &&
        renderGroupButton()}

      <Modal
        actions={[
          {
            type: 'cancel',
            label: 'Close',
            onClick: () => {
              setShowVisibilityModal(false)
            },
          },
          {
            type: 'submit',
            label: 'Save',
            onClick: handleSubmit(async (data) => {
              const logic = _.map(data.visibilityLogic, (l) => ({
                ...l,
                mergeField: l.mergeField.id,
                operator: l.operator.id,
                inclusionLogic: l.inclusionLogic.id,
                value: getLogicRuleValue(l),
              }))

              // Only update the element if there was logic set
              if (logic.length > 0) {
                updateElement({ visibility: logic })
                setValue('always', false)
              }

              setShowVisibilityModal(false)
            }),
          },
        ]}
        icon={<img src={Edit} alt="Edit" />}
        content={
          <div className="mb-2 flex h-full w-full flex-col gap-4 pt-4">
            <MergeFieldLogicFields
              admissionItems={admissionItems}
              baseField="visibilityLogic"
              categories={categories}
              control={control}
              clearErrors={clearErrors}
              errors={errors}
              getValues={getValues}
              mergeFields={availableCustomFields}
              mergeFieldOptions={mergeFieldOptions}
              oilTypeOptions={oilTypeOptions}
              register={register}
              sessions={sessions}
              setValue={setValue}
              watch={watch}
            />
          </div>
        }
        className="w-[90%] sm:max-w-screen-md"
        open={showVisibilityModal}
        title="Visibility Logic"
      />

      <Modal
        actions={[
          {
            type: 'cancel',
            label: 'Cancel',
            onClick: closeLineEditor,
          },
          {
            type: 'submit',
            label: lineDraftIndex === null ? 'Add Line' : 'Save Line',
            disabled: !lineDraftValid,
            onClick: saveLineDraft,
          },
        ]}
        icon={<img src={Edit} alt="Edit" />}
        content={
          lineDraft && (
            <div className="mb-2 flex h-full w-full flex-col gap-4 pt-4 text-left">
              <RadioInput
                horizontal
                label="Line Type"
                labelClassName="text-sm font-semibold"
                options={[
                  { id: LIST_LINE_TYPES.TEXT, label: 'Text & merge fields' },
                  { id: LIST_LINE_TYPES.REPEAT, label: 'Repeat over a field' },
                ]}
                onChange={(type) => updateLineDraft({ type })}
                value={lineDraft.type}
              />

              {lineDraft.type === LIST_LINE_TYPES.REPEAT ? (
                <>
                  <div className="space-y-1.5">
                    <span className="text-sm font-semibold">Field to Repeat</span>

                    <Select
                      className="rounded-2xl border-gray-550 py-2.5 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                      fullWidth
                      id="lineField"
                      nunito
                      onChange={(option) =>
                        updateLineDraft({ field: option.id, fieldLabel: option.label })
                      }
                      options={mergeFieldOptions}
                      placeholder="Select a field"
                      search
                      style={{ flex: true, width: '100%' }}
                      value={_.find(mergeFieldOptions, (o) => o.id === lineDraft.field)}
                    />

                    <p className="text-xs text-gray-600">
                      Prints one line per value the attendee has — sessions, for example. Nothing
                      prints when they have none.
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <span className="text-sm font-semibold">Row Template</span>

                    <TextInput
                      className="w-full rounded-2xl border-gray-550 py-2 pl-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                      id="lineTemplate"
                      name="lineTemplate"
                      onChange={(event) => updateLineDraft({ template: event.target.value })}
                      placeholder="e.g. • {{ item }}"
                      value={lineDraft.template || ''}
                    />

                    <p className="text-xs text-gray-600">
                      Optional. Put <span className="font-mono">{'{{ item }}'}</span> where the
                      value should go. Leave blank to print the value on its own.
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <span className="text-sm font-semibold">Maximum Lines</span>

                    <TextInput
                      className="w-[100px] rounded-2xl border-gray-550 py-2 pl-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                      id="lineMaxRows"
                      max={MAX_REPEAT_ROWS}
                      min={1}
                      name="lineMaxRows"
                      onChange={(event) =>
                        updateLineDraft({
                          maxRows: _.clamp(
                            parseInt(event.target.value, 10) || DEFAULT_REPEAT_ROWS,
                            1,
                            MAX_REPEAT_ROWS,
                          ),
                        })
                      }
                      type="number"
                      value={lineDraft.maxRows ?? DEFAULT_REPEAT_ROWS}
                    />

                    <p className="text-xs text-gray-600">
                      Anything past this is not printed. The designer reserves this much space so
                      you can see the worst case.
                    </p>
                  </div>
                </>
              ) : (
                <div className="space-y-1.5">
                  <span className="text-sm font-semibold">Content</span>

                  <textarea
                    ref={lineContentRef}
                    className="w-full rounded-2xl border border-gray-550 px-4 py-2 text-sm placeholder:font-normal placeholder:text-gray-600 focus:border-purple focus:outline-none"
                    onChange={(event) => {
                      lineCaretRef.current = {
                        start: event.target.selectionStart,
                        end: event.target.selectionEnd,
                      }
                      updateLineDraft({ content: event.target.value })
                    }}
                    onSelect={(event) => {
                      lineCaretRef.current = {
                        start: event.target.selectionStart,
                        end: event.target.selectionEnd,
                      }
                    }}
                    placeholder="e.g. Pronouns: {{ pronouns }}"
                    rows={2}
                    value={lineDraft.content || ''}
                  />

                  <p className="text-xs text-gray-600">
                    Mix static text with merge fields. If every merge field on the line comes back
                    empty, the whole line is skipped and the lines below move up.
                  </p>

                  <Menu as="div" className="relative inline-block w-full">
                    <MenuButton className="inline-flex w-full justify-center gap-x-1.5 rounded-md bg-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-400">
                      Insert Merge Field
                    </MenuButton>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <MenuItems className="absolute left-0 z-10 mt-2 max-h-60 w-full overflow-auto rounded-md bg-white shadow-lg">
                        <div className="flex flex-col">
                          {_.map(mergeFieldOptions, (option) => (
                            <MenuItem
                              className="text-primary cursor-pointer select-none px-3 py-2 text-left text-sm hover:bg-purple hover:text-white"
                              key={`line-merge-${option.id}`}
                              onClick={() => insertLineMergeField(option.id)}
                            >
                              <span>{option.label}</span>
                            </MenuItem>
                          ))}
                        </div>
                      </MenuItems>
                    </Transition>
                  </Menu>
                </div>
              )}

              <Toggle
                checked={!!lineDraft.bold}
                label="Bold this line"
                labelClassName="text-sm"
                onChange={(checked) => updateLineDraft({ bold: checked })}
              />

              <div className="space-y-1">
                <span className="text-sm font-semibold">Display Logic</span>

                <Toggle
                  checked={lineDraft.logic === 'always'}
                  label="Always show this line"
                  labelClassName="text-sm"
                  onChange={(checked) => {
                    if (checked) {
                      updateLineDraft({ logic: 'always' })
                    } else {
                      // The rules themselves live in the form; an empty array
                      // here just marks the line as conditional.
                      setValue('lineLogic', [DEFAULT_LOGIC])
                      updateLineDraft({ logic: [] })
                    }
                  }}
                />

                {lineDraft.logic !== 'always' && (
                  <div className="pt-2">
                    <MergeFieldLogicFields
                      admissionItems={admissionItems}
                      baseField="lineLogic"
                      categories={categories}
                      control={control}
                      clearErrors={clearErrors}
                      errors={errors}
                      getValues={getValues}
                      mergeFields={availableCustomFields}
                      mergeFieldOptions={mergeFieldOptions}
                      oilTypeOptions={oilTypeOptions}
                      register={register}
                      sessions={sessions}
                      setValue={setValue}
                      watch={watch}
                    />
                  </div>
                )}
              </div>
            </div>
          )
        }
        className="w-[90%] sm:max-w-screen-md"
        onClose={closeLineEditor}
        open={showLineModal}
        title={lineDraftIndex === null ? 'New Line' : 'Edit Line'}
      />
    </div>
  )
}

BadgeElementSettings.defaultProps = {
  activeElement: null,
  admissionItems: null,
  availableCustomFields: null,
  badgeHeight: 0,
  badgeWidth: 0,
  categories: null,
  config: null,
  enabledCustomFields: [],
  oilTypeOptions: null,
  sessions: null,
  temporaryGroup: null,
}

BadgeElementSettings.propTypes = {
  activeElement: PropTypes.object,
  admissionItems: PropTypes.object,
  availableCustomFields: PropTypes.object,
  badgeHeight: PropTypes.number,
  badgeWidth: PropTypes.number,
  categories: PropTypes.object,
  copyElement: PropTypes.func.isRequired,
  config: PropTypes.object,
  createGroup: PropTypes.func.isRequired,
  currentPanelIndex: PropTypes.number.isRequired,
  deleteElement: PropTypes.func.isRequired,
  duplicateElement: PropTypes.func.isRequired,
  enabledCustomFields: PropTypes.array,
  layers: PropTypes.array.isRequired,
  moveElement: PropTypes.func.isRequired,
  oilTypeOptions: PropTypes.object,
  panels: PropTypes.array.isRequired,
  temporaryGroup: PropTypes.array,
  registrationType: PropTypes.string,
  sessions: PropTypes.object,
  setCustomFonts: PropTypes.func.isRequired,
  setGoogleFonts: PropTypes.func.isRequired,
  updateElement: PropTypes.func.isRequired,
}

export default BadgeElementSettings
